import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaCheck, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { getServiceBySlug, services } from '../data/services';
import { projects } from '../data/projects';
import {
  WebDevelopment,
  Maintenance,
  Cybersecurity,
  MobileApps,
  IconWrapper
} from '../design-system/icons';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  // Redirect if service not found
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  // Get related projects
  const relatedProjects = projects.filter(p =>
    p.technologies.some(t => service.technologies.includes(t))
  ).slice(0, 3);

  // Get other services
  const otherServices = services.filter(s => s.slug !== slug).slice(0, 3);

  const getServiceIcon = (iconName, size = 48) => {
    const icons = {
      WebDevelopment: <WebDevelopment size={size} />,
      Maintenance: <Maintenance size={size} />,
      Cybersecurity: <Cybersecurity size={size} />,
      MobileApps: <MobileApps size={size} />,
    };
    return icons[iconName] || <WebDevelopment size={size} />;
  };

  // Schema.org Service markup
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "url": `https://olney.dev/services/${service.slug}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Olney Tech Solutions",
      "telephone": "+33689177385",
      "email": "contact@olney.dev",
      "url": "https://olney.dev",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Roncq",
        "postalCode": "59223",
        "addressRegion": "Hauts-de-France",
        "addressCountry": "FR"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 50.75,
        "longitude": 3.12
      },
      "geoRadius": "50000"
    },
    "offers": {
      "@type": "Offer",
      "price": service.pricing.starting,
      "priceCurrency": "EUR",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "availability": "https://schema.org/InStock"
    },
    "serviceType": service.title,
    "termsOfService": "https://olney.dev/cgv"
  };

  // Schema.org BreadcrumbList
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://olney.dev"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://olney.dev/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": service.title,
        "item": `https://olney.dev/services/${service.slug}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <link rel="canonical" href={`https://olney.dev/services/${service.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="pt-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white">{service.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className={`absolute top-0 right-0 w-[600px] h-[600px] bg-[var(--${service.color})] rounded-full blur-[150px] opacity-10 pointer-events-none`}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
              >
                <FaArrowLeft /> Tous les services
              </Link>

              <div className="flex items-center gap-4 mb-6">
                <IconWrapper size="lg" variant={service.color} glow={true}>
                  {getServiceIcon(service.icon)}
                </IconWrapper>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
                {service.title}
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                {service.tagline}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className={`btn-magnetic px-8 py-4 bg-[var(--${service.color})] text-white font-bold rounded-xl hover:opacity-90 transition-all inline-flex items-center gap-2`}
                >
                  {service.cta} <FaArrowRight />
                </Link>
                <a
                  href="tel:+33689177385"
                  className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
                >
                  Appeler maintenant
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className={`glass-panel rounded-3xl p-8 border-[var(--${service.color})]/30`}
            >
              <div className="text-center mb-6">
                <span className="text-gray-400 text-sm uppercase tracking-wider">À partir de</span>
                <div className={`text-5xl font-bold text-[var(--${service.color})] font-['Space_Grotesk'] mt-2`}>
                  {service.pricing.starting}
                  <span className="text-2xl">{service.pricing.unit}</span>
                </div>
                <p className="text-gray-500 text-sm mt-2">{service.pricing.description}</p>
              </div>

              <div className="space-y-3">
                {service.features.slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                    <FaCheck className={`text-[var(--${service.color})]`} />
                    <span className="text-white text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="w-full mt-6 py-4 rounded-xl bg-white/10 text-white font-bold text-center hover:bg-white/20 transition-all block"
              >
                Demander un devis gratuit
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8 font-['Space_Grotesk']">
              À propos de ce service
            </h2>
            <div className="prose prose-invert prose-lg max-w-none">
              {service.fullDescription.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-gray-400 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Ce qui est inclus
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-panel rounded-2xl p-6 hover:border-[var(--primary)]/50 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl bg-[var(--${service.color})]/20 flex items-center justify-center mb-4`}>
                  <FaCheck className={`text-[var(--${service.color})] text-xl`} />
                </div>
                <h3 className="text-white font-bold mb-2">{feature}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Pourquoi choisir ce service ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {service.benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`glass-panel rounded-3xl p-8 text-center border-[var(--${service.color})]/20 hover:border-[var(--${service.color})] transition-colors`}
              >
                <div className={`w-16 h-16 rounded-2xl bg-[var(--${service.color})]/20 flex items-center justify-center mx-auto mb-6`}>
                  <span className="text-3xl">
                    {idx === 0 ? '🚀' : idx === 1 ? '💎' : '🎯'}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">
                  {benefit.title}
                </h3>
                <p className="text-gray-400">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8 font-['Space_Grotesk']">
              Technologies utilisées
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {service.technologies.map((tech, idx) => (
                <span
                  key={idx}
                  className={`px-6 py-3 rounded-xl bg-[var(--${service.color})]/10 border border-[var(--${service.color})]/30 text-[var(--${service.color})] font-medium`}
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex justify-between items-center mb-12"
            >
              <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
                Projets réalisés
              </h2>
              <Link
                to="/portfolio"
                className="text-[var(--primary)] font-bold hover:text-white transition-colors inline-flex items-center gap-2"
              >
                Voir tous <FaArrowRight />
              </Link>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedProjects.map((project, idx) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to={`/portfolio/${project.slug}`}
                    className="glass-panel rounded-3xl p-6 block hover:border-[var(--primary)]/50 transition-colors group"
                  >
                    <div className="aspect-video rounded-xl bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 mb-4 overflow-hidden">
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <span className="text-[var(--secondary)] text-sm font-bold uppercase tracking-widest">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-bold text-white mt-2 group-hover:text-[var(--primary)] transition-colors font-['Space_Grotesk']">
                      {project.title}
                    </h3>
                    <div className="flex gap-2 mt-4">
                      {project.results.slice(0, 2).map((result, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-300">
                          {result.metric} {result.label}
                        </span>
                      ))}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Découvrez nos autres services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {otherServices.map((otherService, idx) => (
              <motion.div
                key={otherService.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={`/services/${otherService.slug}`}
                  className={`glass-panel rounded-3xl p-6 block hover:border-[var(--${otherService.color})]/50 transition-colors group`}
                >
                  <IconWrapper size="md" variant={otherService.color} glow={true} className="mb-4">
                    {getServiceIcon(otherService.icon, 32)}
                  </IconWrapper>
                  <h3 className="text-xl font-bold text-white group-hover:text-[var(--primary)] transition-colors font-['Space_Grotesk']">
                    {otherService.title}
                  </h3>
                  <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                    {otherService.description}
                  </p>
                  <span className={`text-[var(--${otherService.color})] font-bold text-sm mt-4 inline-flex items-center gap-2`}>
                    En savoir plus <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`glass-panel rounded-3xl p-12 text-center border-[var(--${service.color})]/30`}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Discutons de vos besoins. Premier rendez-vous gratuit et sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className={`btn-magnetic px-8 py-4 bg-[var(--${service.color})] text-white font-bold rounded-xl hover:opacity-90 transition-all`}
              >
                {service.cta}
              </Link>
              <a
                href="tel:+33689177385"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                06 89 17 73 85
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
